/**
 * Home: o estado do catalogo em numeros - e o que esses numeros QUEREM DIZER.
 *
 * Sete contadores soltos sao sete numeros. O que se pergunta ao abrir o manager nao e "quantos
 * relatorios existem", e "esta tudo de pe?". Entao os numeros vem agrupados pelo que decidem
 * (catalogo / publicacao e acesso) e, quando uma combinacao e sintoma, a tela diz qual:
 *
 * - relatorios > 0 e publicados = 0: o Viewer esta vazio para todo mundo;
 * - publicados > 0 e policies = 0: todo relatorio publicado esta em modo aberto.
 *
 * Os dois casos sao silenciosos por natureza - ninguem recebe erro, so nao ve dado (ou ve demais).
 */

import { Button, Card } from '@ledger-glass/react'
import { useQuery } from '@tanstack/react-query'

import { fetchSummary } from '../api/catalog'
import type { CatalogSummary } from '../api/types'
import { Alert } from '../components/Alert'
import { Skeleton } from '../components/Skeleton'
import { humanizar } from '../errors/humanizar'

interface Contador {
  campo: keyof CatalogSummary
  rotulo: string
  nota?: string
}

const CATALOGO: Contador[] = [
  { campo: 'clients', rotulo: 'Clientes' },
  { campo: 'hospitals', rotulo: 'Hospitais' },
  { campo: 'powerbi_connections', rotulo: 'Conexões Power BI' },
  { campo: 'datasets', rotulo: 'Datasets' },
  { campo: 'reports', rotulo: 'Relatórios' },
]

export function HomePage() {
  const resumo = useQuery({ queryKey: ['summary'], queryFn: fetchSummary })

  if (resumo.isPending) {
    return (
      <div className="summary-grid">
        {CATALOGO.map((item) => (
          <Skeleton height={96} key={item.campo} />
        ))}
      </div>
    )
  }

  if (resumo.error !== null) {
    return (
      <Alert tone="danger" action={<Button onClick={() => void resumo.refetch()}>Tentar de novo</Button>}>
        Não foi possível carregar o resumo do catálogo: {humanizar(resumo.error)}
      </Alert>
    )
  }

  const dados = resumo.data
  const semPublicar = dados.reports > 0 && dados.published_reports === 0
  const tudoAberto = dados.published_reports > 0 && dados.access_policies === 0

  return (
    <>
      <section className="secao">
        <h2 className="secao__titulo">Catálogo</h2>
        <div className="summary-grid">
          {CATALOGO.map((item) => (
            <Card density="compact" key={item.campo} tone="blue">
              <h3 className="contador__rotulo">{item.rotulo}</h3>
              <strong className="contador__valor">{dados[item.campo]}</strong>
              {item.nota === undefined ? null : <p className="contador__nota">{item.nota}</p>}
            </Card>
          ))}
        </div>
      </section>

      <section className="secao">
        <h2 className="secao__titulo">Publicação e acesso</h2>
        <div className="summary-grid">
          <Card density="compact" tone="blue">
            <h3 className="contador__rotulo">Publicados</h3>
            <strong className="contador__valor">
              {dados.published_reports}
              <span className="contador__de"> de {dados.reports}</span>
            </strong>
            <p className="contador__nota">Só publicado aparece no Report Viewer.</p>
          </Card>
          <Card density="compact" tone="blue">
            <h3 className="contador__rotulo">Políticas de RLS</h3>
            <strong className="contador__valor">{dados.access_policies}</strong>
            <p className="contador__nota">Sem política, o relatório fica em modo aberto.</p>
          </Card>
        </div>
      </section>

      {semPublicar ? (
        <Alert tone="warning">
          Há {dados.reports} relatório(s) no catálogo e <strong>nenhum publicado</strong>: o Report
          Viewer está vazio para todo mundo. A publicação é um campo do relatório — edite-o em
          Relatórios.
        </Alert>
      ) : null}

      {tudoAberto ? (
        <Alert tone="warning">
          Há relatórios publicados e <strong>nenhuma política de RLS</strong>: quem alcança o
          workspace vê todas as linhas. Se isso for proposital, está certo; se não for, crie as
          políticas no onboarding, passo 6.
        </Alert>
      ) : null}

      <Alert tone="info">
        Esta plataforma cataloga relatórios e define <strong>quem vê o quê</strong>, sem depender só
        do Power BI. Para um cliente novo, comece pelo <strong>Onboarding</strong> e siga os passos
        em ordem: identidade vem do Auth, e aqui se mapeiam conexões, datasets, relatórios e as
        políticas de RLS.
      </Alert>
    </>
  )
}
